import { useCallback, useEffect, useState } from "react";
import { Eye, ExternalLink, Save, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { useSite } from "../../context/SiteContext";
import { api, errMsg } from "../../lib/api";
import { dateTime } from "../../lib/format";

const BASE = process.env.PUBLIC_URL || "";
const STATUS = { unsaved: ["Unsaved changes", "bg-amber-100 text-amber-900"], saving: ["Saving…", "bg-sky-100 text-sky-800"],
  saved: ["Draft saved (not published)", "bg-slate-200 text-slate-800"], published: ["Published", "bg-green-100 text-green-800"] };

export default function ContentDocEditor({ contentKey, previewSlug, livePath, children }) {
  const { canEdit, canPublish } = useAuth();
  const { reload } = useSite();
  const [doc, setDoc] = useState(null);
  const [draft, setDraft] = useState(null);
  const [status, setStatus] = useState("saved");

  const load = useCallback(() => api.get(`/admin/content/${contentKey}`).then((r) => {
    setDoc(r.data); setDraft(r.data.draft); setStatus(r.data.status === "published" ? "published" : "saved");
  }).catch((e) => toast.error(errMsg(e))), [contentKey]);
  useEffect(() => { setDoc(null); load(); }, [load]);

  const update = (d) => { setDraft(d); setStatus("unsaved"); };
  const saveDraft = async () => {
    setStatus("saving");
    try { const { data } = await api.put(`/admin/content/${contentKey}/draft`, { draft }); setDoc(data); setStatus("saved"); toast.success("Draft saved. Public site unchanged."); return true; }
    catch (e) { setStatus("unsaved"); toast.error(errMsg(e)); return false; }
  };
  const publish = async () => {
    if (status === "unsaved" && !(await saveDraft())) return;
    try { const { data } = await api.post(`/admin/content/${contentKey}/publish`); setDoc(data); setStatus("published"); reload(); toast.success("Changes published"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!doc || !draft) return <p aria-busy="true">Loading…</p>;
  const [label, cls] = STATUS[status];

  return (
    <div data-testid={`content-editor-${contentKey}`}>
      <div className="sticky top-0 z-10 -mx-1 mb-5 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-1 py-3">
        <div className="flex items-center gap-3">
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${cls}`} role="status" data-testid="content-status-indicator">{label}</span>
          {doc.published_at && <span className="text-xs text-slate-600">Last published {dateTime(doc.published_at)}</span>}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-navy btn-sm" onClick={saveDraft} disabled={!canEdit || status === "saving"} data-testid="content-save-draft-btn"><Save size={15} aria-hidden="true" /> Save Draft</button>
          {previewSlug && <a href={`${BASE}/preview/${previewSlug}`} target="_blank" rel="noreferrer" className="btn btn-sm border border-slate-300 text-navy hover:bg-slate-50" data-testid="content-preview-draft-btn"><Eye size={15} aria-hidden="true" /> Preview Draft</a>}
          {livePath && <a href={`${BASE}${livePath}`} target="_blank" rel="noreferrer" className="btn btn-sm border border-slate-300 text-navy hover:bg-slate-50" data-testid="content-view-live-btn"><ExternalLink size={15} aria-hidden="true" /> View Live</a>}
          <button type="button" className="btn-primary btn-sm" onClick={publish} disabled={!canPublish} title={canPublish ? "" : "Publish permission required"} data-testid="content-publish-btn"><Upload size={15} aria-hidden="true" /> Publish Changes</button>
        </div>
      </div>
      {!canEdit && <p className="mb-4 rounded bg-slate-100 p-3 text-sm" data-testid="content-readonly-note">Read-only: your role cannot edit content.</p>}
      {children(draft, update)}
    </div>
  );
}
